import { ErrorPage, ExtendedFAB, LoadingPage, styleClassNames } from '@chocolate-soup-inc/cs-frontend-components';
import { DragEndEvent, UniqueIdentifier } from '@dnd-kit/core';
import _ from 'lodash';
import { quickScore } from 'quick-score';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { Board } from '../../components/board/Board';
import { TColumnProps } from '../../components/board/Column';
import { TDraggableCardRenderOptions } from '../../components/board/DraggableCard';
import { Filters, TFiltersProps } from '../../components/filters/Filters';
import { TGiftStatuses, useMoveGiftMutation } from '../../generated/graphql';
import { GIFT_PATH } from '../../routes/paths';
import { GiftCard } from './GiftCard';

import { FloatingActions } from '../../components/floatingActions/FloatingActions';
import { useFilter } from '../../contexts/filters';
import { TGiftType, useQueryAllNewHireAssemblingGifts, useQueryAllSubscriptionAssemblingGifts, useQueryAllSubscriptionAssemblingGiftsNew } from '../../entities/gifts/queries';
import { printGiftLabels } from '../../entities/gifts/shared';
import { TManagerAccount } from '../../entities/users/queries';
import { MovingToDoneModalContent } from './MovingToDoneModalContent';

export type TGiftAssemblingColumnDetails = {
  name: string;
  destinationColumns?: TGiftStatuses[];
  nextPhase?: TGiftStatuses;
  id: TGiftStatuses;
};

type TGiftAssemblingProps = {
  columnsDetails: TGiftAssemblingColumnDetails[];
  extraFilterFunction?: (gift: TGiftType) => boolean;
  extraFilters?: TFiltersProps['filters'];
  query: typeof useQueryAllSubscriptionAssemblingGifts | typeof useQueryAllNewHireAssemblingGifts | typeof useQueryAllSubscriptionAssemblingGiftsNew;
  sortingFunction?: (a: TGiftType, b: TGiftType) => number;
  isNew?: boolean;
};

export const GiftAssembling = (props: TGiftAssemblingProps) => {
  const { filtersPageMode } = useFilter();
  useEffect(() => {
    filtersPageMode();
  }, []); //eslint-disable-line

  const { columnsDetails, extraFilterFunction = () => true, extraFilters, query, isNew = false } = props;

  const navigate = useNavigate();

  const [companyFilter, setCompanyFilter] = useState<string>();
  const [officeFilter, setOfficeFilter] = useState<string>();
  const [recipientFilter, setRecipientFilter] = useState<string>();
  const [clientManagerFilter, setClientManagerFilter] = useState<string>();

  const { data: gifts, error, loading } = query();

  const companies = useMemo(() => {
    return _.uniqBy(_.compact(gifts.map((g) => g.company)), 'id');
  }, [gifts]);

  const clientManagers = useMemo(() => {
    return _.unionBy(
      gifts.reduce<TManagerAccount[]>((acc, g) => {
        const manager = g.company?.accountManager;
        if (manager && typeof manager === 'string') acc.push({ name: manager, id: manager });

        return acc;
      }, []),
      'id'
    );
  }, [gifts]);

  const offices = useMemo(() => {
    return _.uniq(
      _.compact(
        gifts
          .filter((e) => {
            if (e.companyId === companyFilter) return true;
          })
          .map((e) => {
            if (e.employee?.officeId && e.employee?.office) return e.employee.office;
          })
      )
    );
  }, [gifts, companyFilter]);

  const filters = useMemo(() => {
    let filtersList: TFiltersProps['filters'] = [];

    filtersList.push({
      disabled: companies.length === 0,
      label: 'Company',
      name: 'companyId',
      onChange: (v) => {
        setOfficeFilter(undefined);
        setCompanyFilter(v != null ? (v as string) : undefined);
      },
      options: companies?.map((c) => ({ label: c.name, value: c.id })),
      type: 'singleSelect',
      value: companyFilter
    });

    if (companyFilter && offices.length > 0)
      filtersList.push({
        type: 'singleSelect',
        label: 'Office',
        name: 'officeId',
        onChange: (v) => {
          setOfficeFilter(v != null ? (v as string) : undefined);
        },
        options: offices.map((o) => ({ label: o.name, value: o.id })),
        value: officeFilter
      });

    filtersList.push({
      label: 'Recipient',
      name: 'recipientName',
      onChange: (v) => {
        setRecipientFilter(v != null ? v : undefined);
      },
      type: 'textInput',
      value: recipientFilter
    });

    filtersList.push({
      disabled: clientManagers.length === 0,
      label: 'Client Manager',
      name: 'clientManager',
      onChange: (v) => {
        setClientManagerFilter(v != null ? (v as string) : undefined);
      },
      options: clientManagers.map((c) => ({ label: c.name, value: c.id })),
      type: 'singleSelect',
      value: clientManagerFilter
    });

    if (extraFilters) filtersList = filtersList.concat(extraFilters);

    return filtersList;
  }, [companies, companyFilter, extraFilters, recipientFilter, offices, officeFilter, clientManagers, clientManagerFilter]);

  const filteredGifts = useMemo(() => {
    return gifts.filter((g) => {
      if (companyFilter != null && g.companyId !== companyFilter) return false;

      if (recipientFilter != null && recipientFilter !== '' && (g.recipient?.fullName == null || quickScore(g.recipient?.fullName, recipientFilter) < 0.7)) return false;

      if (clientManagerFilter !== undefined && clientManagerFilter !== '' && clientManagerFilter !== g.company?.accountManager) return false;

      if (officeFilter && g.employee?.officeId != officeFilter) return false;

      return extraFilterFunction(g);
    });
  }, [companyFilter, extraFilterFunction, gifts, recipientFilter, officeFilter, clientManagerFilter]);

  //const noEventGifts = useMemo(() => gifts.filter((g) => !g.event), [gifts]);
  //console.log('gifts with no events?:', noEventGifts);

  //console.log(isNew ? 'gifts NEW?:' : 'gifts?:', gifts);
  console.log(isNew ? 'filteredGifts NEW?:' : 'filteredGifts?:', filteredGifts);

  const groupedByStatusGifts = useMemo(() => {
    return _.groupBy(filteredGifts, (gift) => gift.status);
  }, [filteredGifts]);

  const renderSubscriptionGift = useCallback(
    (gift: TGiftType, opts?: TDraggableCardRenderOptions) => {
      return <GiftCard key={gift.id} filteredGifts={filteredGifts} gift={gift} options={opts} />;
    },
    [filteredGifts]
  );

  const columns: TColumnProps<TGiftType>[] = useMemo(() => {
    return columnsDetails.map((columnDetails) => {
      const status = columnDetails.id;
      let cards: TColumnProps<TGiftType>['cards'] = (groupedByStatusGifts[columnDetails.id] || []).map((gift) => {
        return {
          data: gift,
          //disabled: gift.status === TGiftStatuses.Done,
          id: gift.id,
          render: renderSubscriptionGift
        };
      });

      if (status === TGiftStatuses.Todo) cards = _.orderBy(cards, ['data.company.name'], ['asc']);

      return {
        cards,
        data: {
          destinationColumns: columnDetails.destinationColumns || {}
        },
        id: columnDetails.id,
        name: columnDetails.name
      };
    });
  }, [columnsDetails, groupedByStatusGifts, renderSubscriptionGift]);

  const [moveGift] = useMoveGiftMutation();

  /*const updateGiftFragment = useCallback((gift: TGiftType, status: TGiftStatuses) => {
    cache.writeFragment({
      data: {
        ...gift,
        status,
        doneAt: status === TGiftStatuses.Done ? new Date().toISOString() : null,
        // This 2 is being used to know that it is temporary done.
        isDone: status === TGiftStatuses.Done ? 2 : 0,
      },
      id: cache.identify(gift),
      fragment: GiftFieldsFragmentDoc,
      fragmentName: getFragmentName(GiftFieldsFragmentDoc),
    });
  }, []);*/

  const [giftsGoingToDone, setGiftsGoingToDone] = useState<TGiftType[]>();

  const onDragEnd = useCallback(
    (event: DragEndEvent, selectedGifts: TGiftType[]) => {
      const droppedColumnId = event.over?.id as TGiftStatuses | undefined;
      const draggedGift = event.active.data.current as TGiftType | undefined;

      if (droppedColumnId === TGiftStatuses.Done) {
        const giftsWithNoAddress = selectedGifts.filter((g) => g.addressId == null || g.addressId === '');

        if (giftsWithNoAddress.length === 0)
          //for (const gift of selectedGifts) updateGiftFragment(gift, droppedColumnId);
          setGiftsGoingToDone(selectedGifts);
        else toast.error(`You are trying to move ${giftsWithNoAddress.length} gift${giftsWithNoAddress.length !== 1 ? 's' : ''} to done that have no address.`);
      } else if (draggedGift && droppedColumnId && draggedGift.status !== droppedColumnId)
        for (const gift of selectedGifts)
          //updateGiftFragment(gift, droppedColumnId);
          moveGift({
            variables: {
              id: gift.id,
              companyId: gift.companyId,
              version: gift._version,
              input: {
                status: droppedColumnId
              }
            }
          }).catch((error) => {
            console.error(serializeError(error));
            toast.error('An error occurred when trying to move the gift.');
            //updateGiftFragment(gift, gift.status);
          });
    },
    [moveGift]
  );

  const onCardClick = useCallback(
    (gift: TGiftType) => {
      navigate(generatePath(`${GIFT_PATH}`, { giftId: gift.id }), { state: { giftsList: filteredGifts } });
    },
    [navigate, filteredGifts]
  );

  const onMoveToDoneCancel = useCallback(() => {
    setGiftsGoingToDone(undefined);
  }, []);

  const onMoveToDoneConfirm = useCallback(() => {
    setGiftsGoingToDone(undefined);
  }, []);

  const [selectedGifts, setSelectedGifts] = useState<TGiftType[]>([]);

  const onSelectedCardsChange = useCallback(
    (selectedCardsIds: UniqueIdentifier[]) => {
      if (filteredGifts != null) setSelectedGifts(filteredGifts.filter((g) => selectedCardsIds.includes(g.id)));
    },
    [filteredGifts]
  );

  const [printLoading, setPrintLoading] = useState<boolean>(false);

  const onPrintClick = useCallback(async () => {
    setPrintLoading(true);

    try {
      await printGiftLabels({
        gifts: selectedGifts
      });
    } catch (error) {
      console.error(serializeError(error));
      toast.error('There was an error trying to print labels.');
    } finally {
      setPrintLoading(false);
    }
  }, [selectedGifts]);

  const onOrphanGiftsClick = useCallback(() => {
    if (!selectedGifts.every((g) => g.status == TGiftStatuses.Done && g.isDone == 1)) return;
    setGiftsGoingToDone(selectedGifts);
    setSelectedGifts([]);
  }, [selectedGifts]);

  if (loading) return <LoadingPage />;

  if (error) {
    console.error('query Apollo ERROR:', error);
    toast.error('An error has ocurred, check the console for details. (query Apollo ERROR)');
    //if (error.message) toast.error(error.message);
    if (!gifts) return <ErrorPage error={error} />;
  }

  return (
    <>
      {giftsGoingToDone && giftsGoingToDone.length > 0 && (
        <MovingToDoneModalContent
          //allGifts={gifts || []}
          gifts={giftsGoingToDone}
          onCancel={onMoveToDoneCancel}
          onSuccess={onMoveToDoneConfirm}
        />
      )}
      {isNew && <p>(NEW)</p>}
      <div className={styleClassNames.fullHeightColumnPage}>
        <Filters filters={filters} />
        <Board<TGiftType> columns={columns} onDragEnd={onDragEnd} onCardClick={onCardClick} onSelectedCardsChange={onSelectedCardsChange} />
        {selectedGifts.length > 0 && (
          <FloatingActions>
            <ExtendedFAB label={`Print ${selectedGifts.length} label${selectedGifts.length > 1 ? 's' : ''}`} leadingIcon='print' loading={printLoading} onClick={onPrintClick} variant='tertiary' />
            {selectedGifts.every((g) => g.status == TGiftStatuses.Done && g.isDone == 1) && (
              <ExtendedFAB label={`Manually ship ${selectedGifts.length} gift${selectedGifts.length > 1 ? 's' : ''}`} leadingIcon='local_shipping' onClick={onOrphanGiftsClick} variant='secondary' />
            )}
          </FloatingActions>
        )}
      </div>
    </>
  );
};
